import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { AlertIconOwner } from "@images/svgIcons/illustrations/alertIcon"
import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer } from "@modules/boxOffer/hooks/useBoxOffer"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { Archive, LoaderCircle } from "lucide-react"
import { useCallback, useImperativeHandle, useState } from "react"

export type BoxOfferArchivedModalRef = {
  open: () => void
}

export type BoxOfferArchivedModalProps = {
  offer: BoxOfferDetailedEntity
}

export function BoxOfferArchivedModal({
  offer,
  ref,
}: {
  offer: BoxOfferDetailedEntity
  ref: React.Ref<BoxOfferArchivedModalRef>
}) {
  const [showModal, setShowModal] = useState(false)
  const { deleteBoxOffer, isPending } = useBoxOffer(offer.id)
  const { hideSidebar } = useSidebar()

  const handleArchiveOffer = () => {
    deleteBoxOffer()
      .then(() => {
        toast.success(word("boxOffer.archivedModal.toast.success"), {
          classNames: successToastClassName(),
        })
        setShowModal(false)
        hideSidebar()
      })
      .catch(() => {
        toast.error(word("boxOffer.archivedModal.toast.error"), {
          classNames: errorToastClassName(),
        })
      })
  }

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  let dot = {
    one: "",
    two: "",
  }

  if (offer.type === BoxOfferType.Subscription) {
    dot = {
      one: word("boxOffer.archivedModal.dot.three"),
      two: word("boxOffer.archivedModal.dot.four"),
    }
  } else if (offer.type === BoxOfferType.Millenium) {
    dot = {
      one: word("boxOffer.archivedModal.dot.three"),
      two: word("boxOffer.archivedModal.dot.two"),
    }
  } else {
    dot = {
      one: word("boxOffer.archivedModal.dot.one"),
      two: word("boxOffer.archivedModal.dot.two"),
    }
  }

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="w-104">
        {/* HEADER */}
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-col items-center gap-3">
              <AlertIconOwner color="currentColor" />
              <Text fontStyle="TL" className="text-center">
                {word(`boxOffer.archivedModal.title.${offer.type}`)}
              </Text>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* DESCRIPTION */}
        <DialogDescription>
          <ul className="flex flex-col gap-2 pl-4">
            {Object.values(dot).map((item, index) => (
              <li key={index}>
                <Text fontStyle="BM">{item}</Text>
              </li>
            ))}
            <li>
              <Text fontStyle="BM">{word(`boxOffer.archivedModal.dot.${offer.type}`)}</Text>
            </li>
          </ul>
        </DialogDescription>

        {/* BUTTON */}
        <div className="mt-4 mb-4 flex flex-row items-center justify-center gap-3">
          <Button variant="outline" size="lg" onClick={() => onHide()} className="w-1/3 grow px-3 py-2">
            {word("global.cancel")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={() => handleArchiveOffer()}
            className="w-1/3 grow px-3 py-2"
            disabled={isPending}
            isLoading={isPending}
          >
            {word("global.archive")}
            {isPending ? (
              <LoaderCircle className="animate-spin" />
            ) : (
              <Archive strokeWidth={1.5} size={15} color="currentColor" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
